import { UserType } from "../types";

// These are our event names
export enum SegmentTrackMetricEnum {
  USER_VIEWED = "User Viewed",
  USER_CLICKED = "User Clicked",
  FORM_SUBMITTED = "Form Submitted",
  USER_INPUTTED = "User Inputted",
}

// These are our event types
export enum EventTypeEnum {
  ADMISSION_PROMPT_DISMISSED = "Admission Prompt Dismissed",
  ADMISSION_PROMPT_VIEWED = "Admission Prompt Viewed",
  CLICK_RECORDING_TOGGLE = "Recording toggle clicked",
  CLIENT_INFO = "Client Info",
  CLIENT_MOOD_CHECKIN = "Client Mood Check-in",
  CLIENT_MOOD_CHECKIN_SUBMITTED = "Client Mood Check-in Submitted",
  CLIENT_NEW_INTRO_ROOM_VIEWED = "Client New Intro Room Viewed",
  CLIENT_NEW_INTRO_ROOM_PII_VERIFICATION_VIEWED = "Client New Intro Room PII Verification Viewed",
  CLIENT_NEW_INTRO_ROOM_PII_VERIFICATION_SUBMITTED = "Client New Intro Room PII Verification Submitted",
  CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_REVIEW_VIEWED = "Client New Intro Room Required Agreements Review Viewed",
  CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_SIGNED = "Client New Intro Room Required Agreements Signed",
  CLIENT_WAITING_ROOM = "Telehealth Client Waiting Room",
  FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT = "5 Minute Reminder Toast Scheduling Prompt",
  GAVE_TRANSCRIPTION_CONSENT = "User Consented to Transcription",
  JOIN_SESSION_CLICKED = "Join Session Clicked",
  LINK_CLICKED = "Link Clicked",
  MEASURE_PROGRESS_CLICKED = "Measure Progress Clicked",
  MEASURE_PROGRESS_VIEWED = "Measure Progress Viewed",
  MEASURE_VIEWED = "Telehealth Measure Viewed",
  PRE_SESSION_VIBES = "Pre Session Vibes",
  PROVIDER_END_WHITEBOARD = "Provider End Whiteboard",
  PROVIDER_NUDGE_CLICKED = "Provider Nudge Clicked",
  PROVIDER_PRE_SESSION_PREP_BUTTON_CLICKED = "Pre-Session Prep Button Clicked",
  PROVIDER_SESSION_COMPLETE = "Provider Completed Session",
  PROVIDER_SESSION_EARLY_END_COMPLETE = "Provider Ended Session Early (Complete)",
  PROVIDER_SESSION_EARLY_END_RESTART = "Provider Ended Session Early (Restart)",
  PROVIDER_START_WHITEBOARD = "Provider Start Whiteboard",
  PROVIDER_WAITING_ROOM = "Telehealth Provider Waiting Room",
  RECONNECTED_REMOTE_PARTICIPANT_VIEWED = "Reconnected Remote Participant Viewed",
  RECONNECTING_REMOTE_PARTICIPANT_VIEWED = "Reconnecting Remote Participant Viewed",
  REFLECTION_TOPICS_VIEWED = "Reflection Topics Viewed",
  SCHEDULE_TAB = "Schedule Tab",
  SCRIBBLE_ACCESSED = "Scribble (Patient Note) Accessed",
  SCRIBBLE_EDITED = "Scribble (Patient Note) Edited",
  TREATMENT_TAB = "Treatment Tab",
  WAITING_ROOM_APPOINTMENT_CREATED = "Waiting Room Appointment Created",
  WAITING_ROOM_APPOINTMENT_UPDATED = "Waiting Room Appointment Updated",
  WAITING_ROOM_SCHEDULE = "Waiting Room Schedule",
}

export type TrackParams = {
  cta: string;
  patientShortId: string;
  providerShortId: string;
  numberOfAppointments: number;
  scheduleError: boolean;
  appointmentPosition: number;
  appointmentStatus: "RESCHEDULED" | "CANCELLED";
  appointmentShortId: string;
  appointmentTimeStart: string;
  newRecordingStatus: "PLAY" | "PAUSE";
  entityType: UserType;
  entityShortId: string;
  type: string;
  measureProgressView: "table" | "graph";
  inSession?: boolean;
  isViewingUpcomingClient?: boolean;
  nudgeId: number;
  editedLength: number;
  paragraphCount: number;
  flowId: string;
  isClientUserAuthenticated: boolean;
  isVerified: boolean;
  /**
   * Often used to specify the video platform e.g. `zoom` or `twilio`
   */
  source: string;
};

export type ClientInfoParams = Pick<
  TrackParams,
  "patientShortId" | "inSession" | "isViewingUpcomingClient"
>;
export type WaitingRoomAppointmentUpdatedParams = Pick<
  TrackParams,
  "patientShortId" | "appointmentShortId" | "appointmentStatus"
>;

export type WaitingRoomAppointmentCreatedParams = Pick<
  TrackParams,
  "patientShortId" | "appointmentShortId"
>;

export type MoodCheckinFormViewedParams = Pick<TrackParams, "patientShortId">;
export type MoodCheckinFormSubmittedParams = Pick<
  TrackParams,
  "patientShortId"
>;

export type WaitingRoomScheduleViewParams = Pick<
  TrackParams,
  "numberOfAppointments" | "scheduleError"
>;
export type WaitingRoomScheduleClickParams = Pick<
  TrackParams,
  "appointmentPosition" | "appointmentShortId"
>;
export type WaitingRoomViewParams = Pick<
  TrackParams,
  "appointmentShortId" | "appointmentTimeStart"
>;

export type MeasureViewedParams = Pick<TrackParams, "type">;

export type RecordingToggleClickedParams = Pick<
  TrackParams,
  "appointmentShortId" | "newRecordingStatus" | "entityType"
>;

export type TranscriptionConsentClickedParams = Pick<
  TrackParams,
  "appointmentShortId" | "entityType"
>;

export type MeasureProgressClickedParams = Pick<
  TrackParams,
  "measureProgressView" | "inSession"
>;

export type MeasureProgressViewedParams = Pick<
  TrackParams,
  "measureProgressView" | "inSession"
>;

export type PreSessionVibesViewedParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId" | "appointmentShortId"
>;

export type WhiteboardParams = Pick<TrackParams, "appointmentShortId">;

export type ProviderNudgeClickedParams = Pick<TrackParams, "nudgeId">;

export type ProviderPreSessionPrepButtonClickedParams = Pick<
  TrackParams,
  "cta" | "appointmentShortId" | "providerShortId" | "inSession"
>;

export type ReflectionTopicsViewedParams = Pick<
  TrackParams,
  "patientShortId" | "inSession"
>;

export type ScribbleAccessedParams =
  | Pick<TrackParams, "patientShortId" | "appointmentShortId" | "inSession">
  | Pick<TrackParams, "patientShortId">;

export type ScribbleEditedParams = ScribbleAccessedParams &
  Pick<TrackParams, "editedLength" | "paragraphCount">;

export type LinkClickedParams = Pick<TrackParams, "cta">;

export type EndSessionParams = Pick<TrackParams, "appointmentShortId">;
export type FiveMinuteReminderToastParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId"
>;

export type AdmissionPromptViewedParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId" | "appointmentShortId"
>;

export type AdmissionPromptDismissedParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId" | "appointmentShortId"
>;

export type JoinSessionClickedParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId" | "appointmentShortId"
>;

export type ClientNewIntroRoomBaseParams = Pick<
  TrackParams,
  "patientShortId" | "providerShortId" | "appointmentShortId" | "flowId"
>;

export type ClientNewIntroRoomParams = ClientNewIntroRoomBaseParams &
  Pick<TrackParams, "isVerified">;

export type ClientNewIntroRoomPIIVerificationViewedParams =
  ClientNewIntroRoomBaseParams & Pick<TrackParams, "isClientUserAuthenticated">;

export type ReconnectingRemoteParticipantViewedParams = Partial<
  Pick<
    TrackParams,
    "appointmentShortId" | "patientShortId" | "providerShortId" | "source"
  >
>;

export type EventToViewParamsMap = {
  [EventTypeEnum.CLIENT_INFO]: ClientInfoParams;
  [EventTypeEnum.CLIENT_MOOD_CHECKIN]: MoodCheckinFormViewedParams;
  [EventTypeEnum.WAITING_ROOM_SCHEDULE]: WaitingRoomScheduleViewParams;
  [EventTypeEnum.CLIENT_WAITING_ROOM]: WaitingRoomViewParams;
  [EventTypeEnum.PROVIDER_WAITING_ROOM]: WaitingRoomViewParams;
  [EventTypeEnum.MEASURE_VIEWED]: MeasureViewedParams;
  [EventTypeEnum.MEASURE_PROGRESS_VIEWED]: MeasureProgressViewedParams;
  [EventTypeEnum.PRE_SESSION_VIBES]: PreSessionVibesViewedParams;
  [EventTypeEnum.SCRIBBLE_ACCESSED]: ScribbleAccessedParams;
  [EventTypeEnum.REFLECTION_TOPICS_VIEWED]: ReflectionTopicsViewedParams;
  [EventTypeEnum.ADMISSION_PROMPT_VIEWED]: AdmissionPromptViewedParams;
  [EventTypeEnum.CLIENT_NEW_INTRO_ROOM_VIEWED]: ClientNewIntroRoomParams;
  [EventTypeEnum.CLIENT_NEW_INTRO_ROOM_PII_VERIFICATION_VIEWED]: ClientNewIntroRoomBaseParams;
  [EventTypeEnum.CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_REVIEW_VIEWED]: ClientNewIntroRoomPIIVerificationViewedParams;
  [EventTypeEnum.RECONNECTED_REMOTE_PARTICIPANT_VIEWED]: ReconnectingRemoteParticipantViewedParams;
  [EventTypeEnum.RECONNECTING_REMOTE_PARTICIPANT_VIEWED]: ReconnectingRemoteParticipantViewedParams;
};

export type UserViewedParams<P extends EventTypeEnum> = {
  componentName?: string;
  componentType?: string;
} & (P extends keyof EventToViewParamsMap ? EventToViewParamsMap[P] : never);

export type EventToClickParamsMap = {
  [EventTypeEnum.WAITING_ROOM_SCHEDULE]: WaitingRoomScheduleClickParams;
  [EventTypeEnum.CLIENT_INFO]: ClientInfoParams;
  [EventTypeEnum.CLICK_RECORDING_TOGGLE]: RecordingToggleClickedParams;
  [EventTypeEnum.GAVE_TRANSCRIPTION_CONSENT]: TranscriptionConsentClickedParams;
  [EventTypeEnum.MEASURE_PROGRESS_CLICKED]: MeasureProgressClickedParams;
  [EventTypeEnum.PROVIDER_NUDGE_CLICKED]: ProviderNudgeClickedParams;
  [EventTypeEnum.PROVIDER_PRE_SESSION_PREP_BUTTON_CLICKED]: ProviderPreSessionPrepButtonClickedParams;
  [EventTypeEnum.PROVIDER_START_WHITEBOARD]: WhiteboardParams;
  [EventTypeEnum.PROVIDER_END_WHITEBOARD]: WhiteboardParams;
  [EventTypeEnum.LINK_CLICKED]: LinkClickedParams;
  [EventTypeEnum.PROVIDER_SESSION_EARLY_END_COMPLETE]: EndSessionParams;
  [EventTypeEnum.PROVIDER_SESSION_EARLY_END_RESTART]: EndSessionParams;
  [EventTypeEnum.PROVIDER_SESSION_COMPLETE]: EndSessionParams;
  [EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT]: FiveMinuteReminderToastParams;
  [EventTypeEnum.JOIN_SESSION_CLICKED]: JoinSessionClickedParams;
  [EventTypeEnum.ADMISSION_PROMPT_DISMISSED]: AdmissionPromptDismissedParams;
};

export type UserClickedParams<P extends EventTypeEnum> =
  P extends keyof EventToClickParamsMap ? EventToClickParamsMap[P] : never;

export type EventToFormSubmittedParamsMap = {
  [EventTypeEnum.CLIENT_MOOD_CHECKIN_SUBMITTED]: MoodCheckinFormSubmittedParams;
  [EventTypeEnum.WAITING_ROOM_APPOINTMENT_UPDATED]: WaitingRoomAppointmentUpdatedParams;
  [EventTypeEnum.WAITING_ROOM_APPOINTMENT_CREATED]: WaitingRoomAppointmentCreatedParams;
  [EventTypeEnum.CLIENT_NEW_INTRO_ROOM_PII_VERIFICATION_SUBMITTED]: ClientNewIntroRoomBaseParams;
};

export type FormSubmittedParams<P extends EventTypeEnum> =
  P extends keyof EventToFormSubmittedParamsMap
    ? EventToFormSubmittedParamsMap[P]
    : never;

export type EventToInputtedParamsMap = {
  [EventTypeEnum.SCRIBBLE_EDITED]: ScribbleEditedParams;
};

export type UserInputtedParams<P extends EventTypeEnum> =
  P extends keyof EventToInputtedParamsMap
    ? EventToInputtedParamsMap[P]
    : never;
